import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

  import NonFreeLesson from '@/components/course/NonFreeLesson.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import useCommonMixin from '@/composables/useCommonMixin'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import Feedback from '@/components/common/Feedback.vue'
  import Notes from '@/components/common/Notes.vue'
  import Dropdown from '@/components/common/Dropdown.vue'
  import DurationInput from '@/components/common/DurationInput.vue'
  import Tabs from '@/components/common/Tabs.vue'
  import TextRenderer from '@/components/editor/TextRenderer.vue'
  import LessonTypes from '@/components/course/LessonTypes.vue'
  import LessonQuestionsTabView from '@/components/course/LessonQuestionsTabView.vue'
  import BookmarkLesson from '@/components/course/BookmarkLesson.vue'
  import LeuckentextQuiz from '@/components/course/LeuckentextQuiz.vue'
  import LessonLoader from '@/components/loaders/LessonLoader.vue'
  import CourseTrialExpired from '@/components/modals/CourseTrialExpired.vue'

  import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute } from 'vue-router/composables'
  import useIcons from '@/composables/useIcons'
  import useSegment from '@/composables/useSegment'
  import useI18n from '@/composables/useI18n'
  import useCourse from '@/composables/useCourse'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useEditor from '@/composables/useEditor'

  import { CourseModule } from '@/store/modules/course'
  import { EditorModule } from '@/store/modules/editor'
  import { UserModule } from '@/store/modules/user'

  import CookieHelper from '@/helpers/CookieHelper'

  import type { PropType } from 'vue'
  import type { Question } from '@/services/interfaces/QuizQuestion'
  import type { CourseModule as CourseModuleState, Lesson, LessonQuestion } from '@/services/interfaces/Course'
  import type { DropdownItem } from '@/services/interfaces/Common'

  import { v4 as uuidv4 } from 'uuid'
  import eventBus from '@/main'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'Lesson',
  props: {
    lessonActionText: {
      type: String,
      required: true,
    },
    lessonCourseId: String,
    lessonIdProp: String,
    lessonModuleId: String,
    formatContent: {
      type: Boolean,
      default: false,
    },
    lessonToRender: {
      type: Object as PropType<Lesson>,
      default: null,
    },
    trialPeriodExpired: Boolean,
    hidePreviousLessonAction: Boolean,
  },
  emits: ['previous-lesson', 'next-lesson-action'],
  setup(__props) {

const props = __props;

  

  

  const route = useRoute()

  const { arrowRight, download } = useIcons()
  const { downloadItem } = useCommonMixin()
  const {
    currentLessonTitle,
    currentCourseId,
    lessonId,
    moduleId,
    currentModule,
    currentLesson: localLesson,
    course,
    isPurchasedCourse,
    showStartCourseForFreeModal,
    showNextCTA,
    completeALesson,
    currentLessonIndex,
    currentModuleIndex,
  } = useCourse()
  const { trackPage, pageViewed, lessonImageDownloaded, lessonViewed } = useSegment()
  const { isDesktopDevice } = useBreakpoint()
  const {
    inEditorMode: editorMode,
    currentLessonQuestions,
    isNewLesson,
    currentEditorLesson,
    lessonSlideshows,
    isEditor,
  } = useEditor(route)
  const { translateString } = useI18n()

  const forbidden = ref(false)
  const editorTabIndex = ref(0)
  const showMobileLeuckentextQuiz = ref(false)

  const inEditorMode = computed(() => editorMode.value && !props.lessonIdProp)

  const currentLesson = computed(() =>
    props.lessonToRender ? props.lessonToRender : inEditorMode.value ? EditorModule.currentLesson : localLesson.value,
  )

  const editorTabs = computed(() => {
    let questionsCount: (LessonQuestion | Question)[] =
      currentLessonQuestions.value?.filter((q) => q._destroy !== true) ?? []
    if (currentLesson.value?.type === 'Quiz' && currentLesson.value?.quiz?.hasQuestions) {
      questionsCount = currentLesson.value.quiz.questions ?? []
    }
    return [
      translateString('content'),
      translateString('editor.lessonQuestionsCount', { count: questionsCount.length }),
    ]
  })

  const fetchingLesson = computed(
    () => (isNewLesson.value ? currentEditorLesson.value : CourseModule.currentLesson) === null,
  )

  const getCourseId = computed(() => props.lessonCourseId || currentCourseId.value)

  const showDownloadCTA = computed(() => {
    return (
      currentLesson.value?.downloadables?.downloadUrl &&
      currentLesson.value?.type === 'Downloadable' &&
      !inEditorMode.value
    )
  })

  const showPreviousLessonAction = computed(
    () => currentModuleIndex.value >= 0 && currentLessonIndex.value > 0 && !inEditorMode.value,
  )

  const lessonToDisplay = computed(() =>
    isNewLesson.value ? currentEditorLesson.value || ({} as Lesson) : currentLesson.value || ({} as Lesson),
  )

  const editorLessonTypeDropdownItems = computed(() => [
    { selectable: isNewLesson.value, type: 'Text', title: 'Text' },
    { selectable: isNewLesson.value, type: 'Video', title: 'Video' },
    {
      selectable: isNewLesson.value,
      type: 'SlideShow',
      title: translateString('editor.multipleImagesWithText'),
    },
    { selectable: isNewLesson.value, type: 'Quiz', title: 'Quiz' },
    { selectable: isNewLesson.value, type: 'Audio', title: 'Audio' },
    { selectable: isNewLesson.value, type: 'Downloadable', title: 'Downloadable' },
    { selectable: isNewLesson.value, type: 'Table', title: 'Table' },
    { selectable: isNewLesson.value, type: 'Profile', title: 'Profile' },
    { selectable: isNewLesson.value, type: 'Script', title: 'Script' },
  ])

  const fetchLesson = async (isNewLessonId?: boolean) => {
    if (EditorModule.editMode && (fetchingLesson.value || isNewLessonId)) {
      if (isNewLesson.value) {
        CourseModule.setCurrentLessonQuestions([])
      } else {
        await EditorModule.setCurrentLesson(null)
        CourseModule.resetCurrentLesson()
        await CourseModule.getCurrentLessonQuestions({
          courseId: getCourseId.value,
          moduleId: props.lessonModuleId || moduleId.value,
          lessonId: props.lessonIdProp || lessonId.value,
        })
      }
    }

    if (inEditorMode.value && isNewLesson.value) return

    CourseModule.getLesson({
      courseId: getCourseId.value,
      moduleId: props.lessonModuleId || moduleId.value,
      lessonId: props.lessonIdProp || lessonId.value,
    }).then((resp) => {
      if (resp.response?.status === 403) {
        forbidden.value = true
      }

      EditorModule.setLeuckentext(resp.leuckentext)
      EditorModule.setLessonSlideshows(currentEditorLesson.value?.slideShow ?? null).then(() => {
        lessonSlideshows.value?.forEach((slide) => {
          eventBus.$set(slide, 'editorId', uuidv4())
          eventBus.$set(slide, 'slideImageData', '')
        })
      })
      EditorModule.setLessonDownloadable(currentEditorLesson.value?.downloadables ?? null)
    })
  }

  const downloadContent = (contentUrl?: string) => {
    if (!contentUrl) return

    lessonImageDownloaded(
      getCourseId.value,
      course.value!.title,
      moduleId.value,
      currentModule.value.name,
      lessonId.value,
      currentLesson.value!.title,
      contentUrl,
    )
    downloadItem(contentUrl)
  }

  const editorSlideshowLayoutDropdownItems = computed((): DropdownItem[] => [
    { selectable: true, type: false, title: translateString('editor.asSlideshow') },
    { selectable: true, type: true, title: translateString('editor.asScrollablePage') },
  ])

  const onLayoutChange = (type: boolean) => {
    EditorModule.setEditableState({
      key: 'currentLessonisOnePager',
      path: route.path,
      state: 'currentLesson',
      property: 'isOnePager' as keyof Lesson,
      value: type,
    })
  }

  const onLessonTypeChange = (type: string) => {
    EditorModule.setEditableState({
      key: 'currentLessontype',
      path: route.path,
      state: 'currentLesson',
      property: 'type' as keyof (Lesson | CourseModuleState),
      value: type,
    })
  }

  if (!props.lessonToRender) fetchLesson()

  onMounted(async () => {
    if (!course.value) await CourseModule.getCourse({ courseId: getCourseId.value })
    if (!course.value?.trial && !isPurchasedCourse.value && !isEditor.value) {
      showStartCourseForFreeModal({ hideCloseButton: true })
    }
    await trackPage('Course Lesson View')
    await pageViewed('Course Lesson View')
  })

  onBeforeUnmount(() => {
    if (!props.lessonToRender) CourseModule.resetCurrentLesson()
    if (inEditorMode.value) {
      EditorModule.setLessonDownloadable(null)
      EditorModule.resetEditorChanges()
    }
  })

  onBeforeRouteLeave((_to, from, next) => {
    if (from.params.lessonId) {
      completeALesson(from.params.lessonId)
    }
    next()
  })

  onBeforeRouteUpdate((_to, from, next) => {
    if (from.params.lessonId) {
      completeALesson(from.params.lessonId)
    }
    next()
  })

  watch(lessonId, (_newLessonId: string, oldLessonId: string) => {
    fetchLesson(!!oldLessonId)
    trackPage('Course Lesson View')
    pageViewed('Course Lesson View')
  })

  watch(inEditorMode, (value: boolean) => {
    if (value) {
      if (CourseModule.currentLessonQuestions === null) {
        CourseModule.getCurrentLessonQuestions({
          courseId: getCourseId.value,
          moduleId: props.lessonModuleId || moduleId.value,
          lessonId: props.lessonIdProp || lessonId.value,
        })
      }
    } else {
      editorTabIndex.value = 0
    }
  })

  watch(currentLesson, (lesson) => {
    if (lesson && UserModule.currentCompany) {
      lessonViewed(
        UserModule.currentCompany.id,
        lesson.id,
        lesson.title,
        moduleId.value,
        currentCourseId.value,
        CookieHelper.getCookieValue('language-code') || 'de',
      )
    }
  })

return { __sfc: true,props, route, arrowRight, download, downloadItem, currentLessonTitle, currentCourseId, lessonId, moduleId, currentModule, localLesson, course, isPurchasedCourse, showStartCourseForFreeModal, showNextCTA, completeALesson, currentLessonIndex, currentModuleIndex, trackPage, pageViewed, lessonImageDownloaded, lessonViewed, isDesktopDevice, editorMode, currentLessonQuestions, isNewLesson, currentEditorLesson, lessonSlideshows, isEditor, translateString, forbidden, editorTabIndex, showMobileLeuckentextQuiz, inEditorMode, currentLesson, editorTabs, fetchingLesson, getCourseId, showDownloadCTA, showPreviousLessonAction, lessonToDisplay, editorLessonTypeDropdownItems, fetchLesson, downloadContent, editorSlideshowLayoutDropdownItems, onLayoutChange, onLessonTypeChange, computed, onBeforeUnmount, onMounted, ref, watch, NonFreeLesson, KetchUpButton, useCommonMixin, SVGRenderer, Feedback, Notes, Dropdown, DurationInput, Tabs, TextRenderer, LessonTypes, LessonQuestionsTabView, BookmarkLesson, LeuckentextQuiz, LessonLoader, CourseTrialExpired, onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useIcons, useSegment, useI18n, useCourse, useBreakpoint, useEditor, CourseModule, EditorModule, UserModule, CookieHelper, uuidv4, eventBus }
}

})